/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */

/**
 * Download Icon
 *
 * @file DownloadIcon.jsx
 * @author Mohamed yasar arabath M
 * @since 3.0.22
 */

import React, { Fragment } from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';
import Icon from '../icons/Icon';
import {
  DownloadRoot,
  DownloadWrapper,
  DownloadIconPlaceholder,
  DownloadTitle,
  DownloadProgressText
} from './styles/DownLoadStyles';
import messages from '../../common/defaultMessages';
import { BOOK_DOWNLOAD_STATUS } from '../../common/constants';

const PROGRESS_OFFSET = 10;

/**
 * Calculates and returns a progress percentage based on various states and a raw progress value.
 * Downloading (0 - 90)
 * Extracting (90 -  95)
 * Completed (100)
 *
 * @param {number | undefined} progress
 * @param {boolean} [isExtracting=false]
 * @param {boolean} [isCompleted=false]
 * @returns {number | undefined}
 */
const getProgress = (progress, isExtracting = false, isCompleted = false) => {
  if (isExtracting) {
    return 95;
  }

  if (isCompleted) {
    return 100;
  }

  // If progress is undefined or zero, it means we don't have a meaningful progress to display yet.
  if (typeof progress === 'undefined' || progress === 0) {
    return undefined;
  }

  const adjustedProgress = progress - PROGRESS_OFFSET;

  if (adjustedProgress <= 0) {
    return adjustedProgress < -5 ? 1 : 3;
  }

  return adjustedProgress;
};

const Download = (props) => {
  const {
    intl,
    backgroundImage,
    iconOnly,
    progress,
    styles,
    downloadStatus,
    onCancelClick,
    onClick
  } = props;
  const isExtracting = downloadStatus === BOOK_DOWNLOAD_STATUS.EXTRACTING;
  const isCompleted = downloadStatus === BOOK_DOWNLOAD_STATUS.COMPLETED;
  const progressToRender = getProgress(progress, isExtracting, isCompleted);
  // Is downloading
  const isProgress = (progressToRender > 0 && progressToRender <= 90);
  const showProgress = isProgress || isExtracting || isCompleted;

  const renderIcon = () => {
    if (showProgress) {
      return (<CircularProgress
        size={48}
        thickness={4}
        color="inherit"
        variant="determinate"
        value={progressToRender}
      />);
    }

    return (<Icon name="DownloadIcon" />);
  };

  const getText = () => {
    if (isProgress) {
      return intl.formatMessage(messages.downloading);
    }

    if (isExtracting) {
      return intl.formatMessage(messages.extracting);
    }

    return intl.formatMessage(messages.download);
  };

  const handleClick = (event) => {
    // Handle cancel callback
    if (event && !isExtracting && isProgress) {
      event.preventDefault();
      event.stopPropagation();
      onCancelClick();
    } else {
      onClick(event);
    }
  };

  return (
    iconOnly
      ? <Fragment>{renderIcon()}</Fragment>
      : (
        <DownloadRoot
          onClick={handleClick}
          data-downloading={showProgress}
          style={{
            background: `linear-gradient(0deg, rgba(2, 9, 23, 0.80) 0%, rgba(2, 9, 23, 0.80) 100%), url('${backgroundImage}') lightgray 50% / cover no-repeat`,
            ...styles
          }}
        >
          <DownloadWrapper>
            <DownloadIconPlaceholder showProgress={showProgress}>
              {renderIcon()}

              {showProgress && <DownloadProgressText>{progressToRender} %</DownloadProgressText>}
            </DownloadIconPlaceholder>
            <DownloadTitle>
              {getText()}
            </DownloadTitle>
          </DownloadWrapper>
        </DownloadRoot>
      )
  );
};

Download.defaultProps = {
  iconOnly: false,
  styles: {},
  downloadStatus: '',
  onCancelClick: () => {},
  onClick: () => {}
};

Download.propTypes = {
  intl: PropTypes.object.isRequired,
  backgroundImage: PropTypes.string.isRequired,
  iconOnly: PropTypes.bool,
  progress: PropTypes.number.isRequired,
  styles: PropTypes.object,
  downloadStatus: PropTypes.string,
  onCancelClick: PropTypes.func,
  onClick: PropTypes.func
};

const DownloadWithLanguage = injectIntl(Download);

export default DownloadWithLanguage;
